import JalaaliCalendar from "@/components/calendar/JalaaliCalendar";
import { CalendarEvent, ViewType } from "@/components/calendar/types";
import { Actor, AppointmentStatus, UserAppointmentsDto, WorkScheduleListData } from "@/models/club";
import { Customer } from "@/models/response/customer";
import CancelAppointment from "@/modules/calendar/CancelAppointment";
import PreviewEvent from "@/modules/calendar/PreviewEvent";
import UsersFilter from "@/modules/calendar/UsersFilter";
import AddAppointment from "@/modules/schedule/appointment";
import SelectCustomer from "@/modules/schedule/customer/SelectCustomer";
import { useGetDailyAppointment } from "@/services/appointment/hooks/useGetDailyAppointment";
import { useShiftSchedule } from "@/services/shiftSchedule/utils";
import { ActionIcon, Badge, Button, Group, Skeleton, Stack, Switch, Text } from "@mantine/core";
import { useAuth } from "@/context/auth";
import useCalendarSelection from "@/modules/calendar/useCalendarSelection";
import { useQueries } from "@tanstack/react-query";
import merchantUserService from "@/services/merchantUser";
import { hasCalendarServices } from "@/modules/calendar/hasCalendarServices";
import { useDisclosure } from "@mantine/hooks";
import { IconAlertCircle, IconX } from "@tabler/icons-react";
import moment from "moment-jalaali";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import UseGetCustomerById from "../customer/hooks/useGetCustomerById";
import { ClickEventSlotProps } from "./type";
import useIsSmall from "@/hooks/useIsSmall";

const EMPTY_SHIFTS: WorkScheduleListData = [];

const Calendar = () => {
  const { cId } = useParams();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(moment());
  const [selectedDate, setSelectedDate] = useState(moment());
  const [preview, { open, close }] = useDisclosure(false);
  const [cancel, { open: cancelOpen, close: cancelClose }] =
    useDisclosure(false);
  const [view, setView] = useState<ViewType>("day");
  const [currentTime, setCurrentTime] = useState(moment().format("HH:mm"));
  const isSmall = useIsSmall();
  const { data: customer, isLoading: isLoadingCustomer } = UseGetCustomerById({
    id: Number(cId),
  });
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(
    null
  );
  const { getUserByToken } = useAuth();
  const account = getUserByToken();
  const { selection, choose } = useCalendarSelection(`${account?.parentUserId ?? ""}:${account?.userId ?? ""}`);
  const [selectedPartner, setSelectedPartner] =
    useState<UserAppointmentsDto | null>(null);
  const [
    previewFilterCustomer,
    { open: openPreviewFilterCustomer, close: closePreviewFilterCustomer },
  ] = useDisclosure(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null
  );
  const [previewEvent, { open: openPreviewEvent, close: closePreviewEvent }] =
    useDisclosure(false);

  const { data: shiftSchedule, isLoading: isShiftScheduleLoading } =
    useShiftSchedule({
      fromDate: moment(currentDate).format("YYYY-MM-DD"),
      toDate:
        view === "day"
          ? moment(currentDate).format("YYYY-MM-DD")
          : isSmall ? moment(currentDate).add(3, "days").format("YYYY-MM-DD") : moment(currentDate).add(7, "days").format("YYYY-MM-DD"),
    });

  const {
    data,
    isLoading: isLoadingAppointment,
    isFetching: isFetchingAppointment,
    isPreviousData,
  } = useGetDailyAppointment({
    keepPrevious: true,
    fromDate:
      view === "month"
        ? currentDate.clone().startOf("jMonth").format("YYYY-MM-DD")
        : moment(currentDate).format("YYYY-MM-DD"),
    toDate:
      view === "day"
        ? moment(currentDate).format("YYYY-MM-DD")
        : view === "week"
          // Same range as the shift request, so every visible day (7 on desktop) has its appointments.
          ? moment(currentDate).add(isSmall ? 3 : 7, "days").format("YYYY-MM-DD")
          : currentDate.clone().endOf("jMonth").format("YYYY-MM-DD"),
  });

  const [previewCustomer, { open: openCustomer, close: closeCustomer }] =
    useDisclosure(false);

  const memberDetails = useQueries({ queries: (data ?? []).map(item => ({
    queryKey: ["getMerchantUsersById", item.user?.id],
    queryFn: () => merchantUserService.getMerchantUserById(item.user!.id!),
    enabled: item.user?.id != null,
    staleTime: 60_000,
  })) });
  const eligible = (data ?? []).filter((item, index) => item.user?.id != null &&
    hasCalendarServices(memberDetails[index]?.data, memberDetails[index]?.isSuccess));
  const value = useMemo(() => eligible.filter(item => selection.onlyMe
    ? item.user?.phoneNumber === account?.phoneNumber
    : selection.ids === null || selection.ids.includes(String(item.user?.id))
  ).map(item => String(item.user?.id)), [eligible, selection.onlyMe, selection.ids, account?.phoneNumber]);
  const setValue = (ids: string[]) => choose({ onlyMe: false, ids });

  // Stable empty array so we never pass a new [] reference when filtered list is empty
  const EMPTY_PARTNERS = useMemo(() => [], []);

  // Derive filtered partners by selected user IDs (avoids extra state + re-renders)
  const filteredData = useMemo(() => {
    const filtered = eligible.filter((userAppointment) => {
      const userId = userAppointment.user?.id?.toString();
      return userId && value.includes(userId);
    });
    const result = filtered.map((item) => ({
      ...item,
      appointmentCalendar: item.appointmentCalendar
        ?.map((calendar) => ({
          ...calendar,
          appointments:
            calendar?.appointments?.filter(
              (appointment) =>
                appointment?.status === AppointmentStatus?.Value1 ||
                appointment?.status === AppointmentStatus?.Value2 ||
                (appointment?.status === AppointmentStatus?.Value0 && appointment?.actor === Actor?.Value20)
            ) ?? [],
        }))
        .filter(
          (calendar) =>
            calendar?.appointments && calendar.appointments.length > 0
        ) ?? [],
    }));
    return result.length === 0 ? EMPTY_PARTNERS : result;
  }, [eligible, value, EMPTY_PARTNERS]);

  const clickEventSlot = useCallback(
    (props: ClickEventSlotProps) => {
      setCurrentTime(`${props.hour.toString()}`);
      setSelectedPartner(props?.partner);
      // Week columns carry their own day; day view falls back to the current date as before.
      setSelectedDate(props.date ?? currentDate);
      open();
    },
    [currentDate, open]
  );

  // «وقت خالی» on the stylist home links here as ?add=HH:mm&member=<id> to book today at that time.
  // The address is cleaned first and the form opened only after, so the drawer's #hash entry sits on
  // the clean URL (closing it goes back to the calendar, not to the link). The hash is set before
  // opening: CustomDrawer calls close() while its hash is missing, which outside a click wins the race.
  const [searchParams, setSearchParams] = useSearchParams();
  const [pendingSlot, setPendingSlot] = useState<ClickEventSlotProps | null>(null);
  useEffect(() => {
    const hour = searchParams.get("add");
    if (!hour || !data) return;
    const memberId = Number(searchParams.get("member"));
    const partner = data.find((item) => item.user?.id === memberId) ?? (data.length === 1 ? data[0] : undefined);
    if (partner && /^\d{2}:\d{2}$/.test(hour)) setPendingSlot({ partner, hour, date: moment() });
    setSearchParams({}, { replace: true });
  }, [searchParams, data, setSearchParams]);
  useEffect(() => {
    if (!pendingSlot || searchParams.has("add")) return;
    window.location.hash = "add-appointment"; // AddAppointment's drawer key
    clickEventSlot(pendingSlot);
    setPendingSlot(null);
  }, [pendingSlot, searchParams, clickEventSlot]);

  // Closing only closes. CustomDrawer also calls close() spuriously (on every render while its hash is
  // absent, including once while opening), so a refetch here either looped or was skipped. A booking or
  // cancellation now refreshes the appointments itself (useCreateAppointment / useCancelAppointment).
  const closeAddAppointment = close;

  // Preview shows the appointment's own day (week/month events are not always on currentDate).
  const selectedEventDate = useMemo(
    () => (selectedEvent?.appointmentDate ? moment(selectedEvent.appointmentDate) : currentDate),
    [selectedEvent, currentDate]
  );
  const members = useMemo(
    () => ({ selected: filteredData.length, total: eligible.length, open: openPreviewFilterCustomer }),
    [filteredData.length, eligible.length, openPreviewFilterCustomer]
  );

  const selectEvent = useCallback((event: CalendarEvent | null) => {
    setSelectedEvent(event);
    openPreviewEvent();
  }, [openPreviewEvent]);

  useEffect(() => {
    if (customer) {
      setSelectedCustomer(customer as Customer);
    }
  }, [customer]);

  return (
    <div className="calendar-page">
      {cId && !isLoadingCustomer ? (
        <Stack bg="blue.5" px="md" py="xs">
          <Group justify="space-between">
            <Stack gap="xs">
              <Text size="xs" c="white">
                تعیین نوبت برای{" "}
                <Badge>
                  {customer?.firstName || customer?.lastName
                    ? customer?.firstName + " " + customer?.lastName
                    : customer?.phoneNumber}
                </Badge>
              </Text>
              <Group gap="5">
                <IconAlertCircle color="white" size={16} />
                <Text size="xs" c="white">
                  لطفا زمان را انتخاب نمایید
                </Text>
              </Group>
            </Stack>

            <ActionIcon
              variant="transparent"
              color="white"
              onClick={() => {
                setSelectedCustomer(null);
                navigate("/dashboard/calendar", { replace: true });
              }}
            >
              <IconX />
            </ActionIcon>
          </Group>
        </Stack>
      ) : (
        <Skeleton />
      )}

      <Group justify="space-between" p="sm" style={{ borderBottom: "1px solid var(--mantine-color-gray-2)", background: "var(--mantine-color-body)" }}>
        <Switch label="فقط تقویم من" checked={selection.onlyMe} onChange={event => choose({ ...selection, onlyMe: event.currentTarget.checked })} />
        <Text size="xs" c="dimmed">انتخاب شما در این مرورگر حفظ می‌شود</Text>
      </Group>
      {!isLoadingAppointment && filteredData.length === 0 && <Stack p="md" align="center" gap="xs">
        <Text size="sm">{selection.onlyMe ? "تقویم کاری برای شما با خدمت تعریف‌شده پیدا نشد." : "عضوی برای نمایش انتخاب نشده یا خدمتی برای اعضا تعریف نشده است."}</Text>
        <Button variant="light" size="xs" onClick={() => choose({ onlyMe: false, ids: null })}>نمایش همه اعضای دارای خدمت</Button>
      </Stack>}
      <JalaaliCalendar
        currentDate={currentDate}
        partners={filteredData}
        setCurrentDate={setCurrentDate}
        onEmptySlotClick={clickEventSlot}
        loading={isLoadingAppointment || isShiftScheduleLoading || isFetchingAppointment}
        stale={isPreviousData}
        shiftSchedule={shiftSchedule || EMPTY_SHIFTS}
        setSelectedEvent={selectEvent}
        view={view}
        setView={setView}
        members={members}
      />
      <AddAppointment
        opened={preview}
        close={closeAddAppointment}
        selectedCustomer={selectedCustomer!}
        openCustomer={openCustomer}
        selectedDate={selectedDate}
        currentTime={currentTime}
        setSelectedDate={setSelectedDate}
        setCurrentTime={setCurrentTime}
        selectedPartner={selectedPartner}
        setSelectedCustomer={setSelectedCustomer}
      />
      <SelectCustomer
        previewCustomer={previewCustomer}
        closeCustomer={closeCustomer}
        setSelectedCustomer={setSelectedCustomer}
      />
      <UsersFilter
        opened={previewFilterCustomer}
        close={closePreviewFilterCustomer}
        open={openPreviewFilterCustomer}
        data={eligible}
        value={value}
        setValue={setValue}
        withTrigger={isSmall}
      />
      <PreviewEvent
        cancelOpen={cancelOpen}
        closePreviewEvent={closePreviewEvent}
        currentDate={selectedEventDate}
        previewEvent={previewEvent}
        selectedDate={selectedEventDate}
        selectedEvent={selectedEvent || {}}
      />

      <CancelAppointment
        appointmentId={selectedEvent?.id || 0}
        close={() => {
          cancelClose();
          closePreviewEvent();
        }}
        open={cancel}
      />
    </div>
  );
};

export default Calendar;
